import { invoiceLogoPath, renderInvoiceSvg, type SnapshotParty } from "@talentkontor/domain";
import { api } from "../../../data";
import { invoiceLogo, invoiceModelOf, invoicePdfOf, sha256Hex, storageSafeName } from "../../../model/invoice-document";
import { attachmentEInvoiceFields, EingabeFehler, isXmlPath, isXmlUpload, prepareUpload, serverPruefen, withStoredExtension, xmlUploadProblem } from "../../../model/e-rechnung";
import { currentLang } from "../../../lib/sprache";
import { docInputsOf, loadInvoiceDetail, type InvoiceDetail } from "./detail-data";
import { geltendeVorlage } from "../../../model/beleg-vorlagen";

/**
 * Archiv der Belege (Befund 4, GoBD): Beim Festschreiben entsteht das PDF einmal aus den eingefrorenen Angaben, liegt im Ordner
 * der Rechnung (<agentur>/invoice/<id>/) und gilt danach für Anzeige, Download, Versand und Creator-Portal. Festgeschriebene
 * Rechnungen ohne Archiv (ältere Belege) werden beim ersten Öffnen des PDFs bzw. vor dem Versand archiviert.
 */

const frozen = (status: string) => ["approved", "sent", "paid", "cancelled"].includes(status);
/** Übernommene Rechnungen (0064) behalten ihr Original aus dem bisherigen System; ein eigenes PDF entsteht für sie nie. */
export const archivable = (inv: { kind: string; status: string; self_billed?: boolean | null; imported?: boolean | null }) =>
  frozen(inv.status) && !inv.imported && (inv.kind === "agency_to_brand" || inv.kind === "agency_to_talent" || !!inv.self_billed);

async function modelOf(d: InvoiceDetail, orgName: string) {
  const logo = await invoiceLogo(invoiceLogoPath((d.invoice["issuer_snapshot"] as SnapshotParty | null) ?? { logo_path: d.org?.logo_path ?? null }));
  const model = invoiceModelOf(docInputsOf(d, orgName, logo?.image ?? null));
  // PDF-Vorlage (0456) nur für Belege, die die Agentur schreibt: Marke vor Standard der Agentur
  const inv = d.invoice;
  const eigen = !inv.imported && (inv.kind === "agency_to_brand" || inv.kind === "agency_to_talent" || !!inv.self_billed);
  const layout = eigen ? await geltendeVorlage(inv.org_id, "invoice", (inv["brand_id"] as string | null) ?? null) : null;
  return layout ? { ...model, layout } : model;
}

/** PDF ablegen, falls es noch keins gibt. Gibt den Pfad zurück (oder null, wenn die Rechnung nicht archiviert wird). */
export async function archiveInvoicePdf(orgId: string, invoiceId: string, orgName: string): Promise<string | null> {
  const d = await loadInvoiceDetail(orgId, invoiceId);
  if (!d || !archivable(d.invoice)) return null;
  if (d.invoice.pdf_path) return d.invoice.pdf_path as string;
  const { bytes, filename } = invoicePdfOf(await modelOf(d, orgName));
  const path = `${orgId}/invoice/${invoiceId}/${Date.now()}_${storageSafeName(filename)}`;
  await api.upload("documents", path, new Blob([bytes as BlobPart], { type: "application/pdf" }));
  const r = await api.rpc<{ pdf_path: string }>("invoice_archive_pdf", { p_invoice: invoiceId, p_path: path, p_sha256: await sha256Hex(bytes) });
  return r?.pdf_path ?? path;
}

/**
 * PDF zum Herunterladen: das archivierte (seit dem Festschreiben unverändert); ein Entwurf wird aus den aktuellen Angaben
 * erzeugt. Ist die archivierte Datei im Speicher nicht lesbar (Demo nach dem Neuladen), entsteht sie aus denselben
 * eingefrorenen Angaben neu.
 */
export async function invoicePdfForDownload(d: InvoiceDetail, orgName: string): Promise<{ blob: Blob; filename: string; archived: boolean }> {
  const inv = d.invoice;
  let path = (inv.pdf_path as string | null) ?? null;
  const model = await modelOf(d, orgName);
  const { bytes, filename } = invoicePdfOf(model);
  if (!path && archivable(inv)) path = await archiveInvoicePdf(inv.org_id, inv.id, orgName).catch(() => null);
  if (path) {
    const blob = await api.download("documents", path).catch(() => null);
    if (blob && (blob.type === "application/pdf" || (await blob.slice(0, 5).text()) === "%PDF-")) return { blob, filename, archived: true };
    // L12: E-Rechnung als XML ist die Rechnungsdatei selbst; sie geht unverändert heraus, nie ein nachgebautes PDF
    if (blob && isXmlPath(path)) return { blob, filename: withStoredExtension(filename, path), archived: true };
  }
  return { blob: new Blob([bytes as BlobPart], { type: "application/pdf" }), filename, archived: false };
}

/**
 * K-410: Rechnung als Seitenbilder (SVG) für die Vorschau im Mahnfenster, aus denselben eingefrorenen Angaben wie das PDF.
 * null, wenn das Portal diesen Beleg nicht selbst schreibt (übernommene Rechnung, Rechnung des Creators mit eigener Datei).
 */
export async function invoiceSheets(d: InvoiceDetail, orgName: string): Promise<string[] | null> {
  const inv = d.invoice;
  const own = !inv.imported && (inv.kind === "agency_to_brand" || inv.kind === "agency_to_talent" || !!inv.self_billed) && d.lines.length > 0;
  if (!own) return null;
  return renderInvoiceSvg(await modelOf(d, orgName));
}

const FILE_TYPES = ["application/pdf", "image/jpeg", "image/png"];
/** Datei prüfen wie im Creator-Portal: PDF, JPG oder PNG bis 15 MB, E-Rechnung als XML bis 5 MB (Mahnschreiben nur PDF) */
export function fileProblem(file: { size: number; type: string; name?: string | null }, pdfOnly = false): string | null {
  if (!pdfOnly && isXmlUpload(file)) return xmlUploadProblem(file, currentLang());
  if (pdfOnly ? file.type !== "application/pdf" : !FILE_TYPES.includes(file.type)) return pdfOnly ? "Bitte eine PDF-Datei wählen." : "Bitte eine PDF-, JPG- oder PNG-Datei oder eine E-Rechnung (XML) wählen.";
  if (file.size <= 0) return "Die Datei ist leer.";
  if (file.size > 15_728_640) return "Die Datei ist größer als 15 MB.";
  return null;
}

/**
 * Datei des Teams an die Rechnung hängen (Beleg, weiterer Anhang für die Marke, außerhalb versandtes Mahnschreiben; 0080):
 * in den Ordner der Rechnung laden und als Anlage eintragen. Die Datenbank schreibt den Schritt in den Verlauf.
 */
export async function attachTeamFile(orgId: string, invoiceId: string, file: File, category: "receipt" | "other" | "dunning_letter", onWarning?: (text: string) => void): Promise<string> {
  const problem = fileProblem(file, category === "dunning_letter");
  if (problem) throw new EingabeFehler(problem);
  // L12: E-Rechnung (XML, ZUGFeRD) lesen; als E-Rechnung gilt sie nur als Beleg, nicht als sonstige Anlage
  const prepared = await prepareUpload(file, file.name, currentLang());
  if (prepared.warning) onWarning?.(prepared.warning);
  const cat = prepared.eInvoice && category === "other" ? "receipt" : category;
  const path = `${orgId}/invoice/${invoiceId}/${Date.now()}_${storageSafeName(file.name)}`;
  await api.upload("documents", path, prepared.blob);
  const rows = await api.insert<{ id: string }>("attachments", {
    org_id: orgId, owner_kind: "invoice", owner_id: invoiceId, storage_path: path, file_name: file.name.slice(-200) || "Beleg.pdf",
    mime_type: prepared.mime, size_bytes: file.size, category: cat, ...(cat === "dunning_letter" ? { sha256: prepared.sha256 } : attachmentEInvoiceFields(prepared)),
  });
  // E-1 (0448): Prüfsumme und Kurzfassung bildet der Server selbst (nur Belege; Mahnschreiben und sonstige Anlagen nicht)
  if (cat === "receipt") await serverPruefen(api, { attachment: rows[0]?.id, path }, prepared);
  return rows[0]?.id ?? "";
}

export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.target = "_blank";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
